import { useState, useEffect, useCallback } from 'react';
import { storageHelpers } from 'helpers/storage';
import { AnnouncementBarData } from './types';

export const DEFAULT_BAR_ID = 'default-announcement';

/**
 * Custom hook to manage announcement bar visibility
 * @param {Array} announcementBars - Array of announcement bar data
 * @returns {Object} - Object containing visibility state and handler functions
 */
export const useAnnouncementBarVisibility = (announcementBars?: AnnouncementBarData[]) => {
  const [barVisibility, setBarVisibility] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (announcementBars?.length) {
      const newVisibility: Record<string, boolean> = {};

      announcementBars.forEach((bar) => {
        const barId = bar.barId || DEFAULT_BAR_ID;
        const storageKey = `announcement-bar-closed-${barId}`;
        const isClosed = storageHelpers.getStorageState(storageKey, 'session') === true;
        newVisibility[barId] = !isClosed;
      });

      setBarVisibility(newVisibility);
    }
  }, [announcementBars]);

  const handleBarClose = useCallback((barId: string) => {
    const storageKey = `announcement-bar-closed-${barId}`;
    storageHelpers.setStorageState(storageKey, true, 'session');

    setBarVisibility((prev) => ({
      ...prev,
      [barId]: false,
    }));
  }, []);

  const hasVisibleBars = Object.values(barVisibility).some((isVisible) => isVisible);

  return {
    barVisibility,
    handleBarClose,
    hasVisibleBars,
  };
};
